interface SupportedLocaleInterface {
  [locale: string]: {
    supported: string[];
  };
}

/**
 * Return the first segment for a given url
 * @param {string} fullPath
 * @return {string} first segmnt
 */
export const firstSegment = (fullPath: string) => {
  if (!fullPath) return ''
  return fullPath.split('/').filter(Boolean)[0]
}

/**
 * Return the list of supported locale
 * @return {Object} list of supported locales
 */
export const getSUpportedLocales = (): SupportedLocaleInterface => {
  /* eslint @typescript-eslint/no-var-requires: "off" */
  const supportedLocales = require('@/assets/supportedLocales.json')
  return supportedLocales
}

/**
 * Return ISO locale to default netatmo format eg: 'fr_FR' => 'fr-fr' or 'FR' => 'fr-fr' or 'fr-fr' => 'fr-fr'
 * @param {string} locale
 */
export const normalizeLocaleFormat = (locale: string) => {
  if (locale.indexOf('_') > -1) { // locale is reg_locale
    return locale.replace('_', '-').toLowerCase()
  }
  if (locale.indexOf('-') > -1) {
    return locale.toLowerCase()
  }
  return `${locale.toLowerCase()}-${locale.toLowerCase()}`
}

/**
 * Return if the given locale is supported
 * @param {string} testLocale locale to test
 * @return {boolean}
 */
export const isSupportedLocale = (testLocale: string | undefined | null): boolean => {
  if (!testLocale) return false
  testLocale = normalizeLocaleFormat(testLocale)
  const supportedLocales: SupportedLocaleInterface = getSUpportedLocales()
  const isSUpportedLocale: boolean = Object.prototype.hasOwnProperty.call(supportedLocales, testLocale)

  return isSUpportedLocale
}

/**
 * Return if the given language is supported (eg: 'fr' is the language of the locale 'fr-FR')
 * @param {string} testLanguage language to test
 */
export const isSupportedLanguage = (testLanguage: string | undefined): boolean => {
  if (typeof testLanguage === 'undefined') return false
  testLanguage = normalizeLocaleFormat(testLanguage)
  const supportedLocales: SupportedLocaleInterface = getSUpportedLocales()
  for (const locale in supportedLocales) {
    const { supported } = supportedLocales[locale]
    if (supported.indexOf(testLanguage) > -1) return true
  }
  return false
}

/**
 * Convert locale to RegLocale eg: it-it to it_IT
 * @param locale
 */
export const localeToRegLocale = (locale: string) => {
  const regLocale = locale.split('-')
  return `${regLocale[0]}_${regLocale[1].toUpperCase()}`
}

/**
 * Return locale for the given language (return 'fr-fr' from 'fr')
 * @param {string} language
 * @return {stringn|null} locale
 */
export const getLocaleFromLanguage = (language: string): string | null => {
  language = normalizeLocaleFormat(language)
  const supportedLocales: SupportedLocaleInterface = getSUpportedLocales()
  for (const locale in supportedLocales) {
    const { supported } = supportedLocales[locale]
    if (supported.indexOf(language) > -1) {
      return locale
    }
  }
  return null
}

/**
 * Return supported locale from a given locale (from supported locale file): eg: return ['fr', 'fr_FR'] from 'fr-fr'
 * @param locale
 */
export const getSupportedFromLocale = (locale: string): string[] => {
  locale = normalizeLocaleFormat(locale)
  const supportedLocales: SupportedLocaleInterface = getSUpportedLocales()
  if (Object.prototype.hasOwnProperty.call(supportedLocales, locale)) {
    return supportedLocales[locale].supported
  }
  return [locale]
}

/**
 * Return url query value from key
 * @param name
 */
export const getUrlParameter = (name: string): string|null => {
  name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]')
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
  const results = regex.exec(location.search)
  return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '))
}
